import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Skeleton,
} from "@repo/ui";
import {
  CreditCard,
  Calendar,
  Clock,
  ExternalLink,
  Loader2,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import {
  createPaymentUpdateSession,
  createCheckoutSession,
} from "@/lib/api/stripe";
import type { BillingInfo, PaymentMethod, PlanPrices } from "@/lib/api/stripe";
import { UpgradePlanDialog } from "./upgrade-plan-dialog";

interface BillingPlanCardProps {
  billing: BillingInfo | null;
  pharmacy: {
    subscription_status: string;
    subscription_started_at: string | null;
    subscription_ends_at: string | null;
    trial_ends_at: string | null;
  };
  prices: PlanPrices | null;
  paymentMethod: PaymentMethod | null;
  hasStripeCustomer: boolean;
  isLoading: boolean;
  onSubscriptionChange: () => void;
}

const statusConfig: Record<
  string,
  {
    label: string;
    variant: "default" | "destructive" | "outline" | "secondary";
    description: string;
  }
> = {
  pending: {
    label: "Pending",
    variant: "secondary",
    description: "Please subscribe to activate your account",
  },
  trial: {
    label: "Trial",
    variant: "secondary",
    description: "You are on a free trial period",
  },
  active: {
    label: "Active",
    variant: "default",
    description: "Your subscription is active",
  },
  past_due: {
    label: "Past Due",
    variant: "destructive",
    description: "Payment is overdue. Please update your payment method.",
  },
  canceled: {
    label: "Canceled",
    variant: "outline",
    description: "Your subscription has been canceled",
  },
  expired: {
    label: "Expired",
    variant: "destructive",
    description: "Your subscription has expired",
  },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function getDaysRemaining(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil(
    (target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
}

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount / 100);
}

function formatBrand(brand: string): string {
  const brands: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    discover: "Discover",
    diners: "Diners Club",
    jcb: "JCB",
    unionpay: "UnionPay",
  };
  return brands[brand] || brand.charAt(0).toUpperCase() + brand.slice(1);
}

export function BillingPlanCard({
  billing,
  pharmacy,
  prices,
  paymentMethod,
  hasStripeCustomer,
  isLoading,
  onSubscriptionChange,
}: BillingPlanCardProps) {
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<"monthly" | "yearly">(
    "yearly"
  );
  const [portalError, setPortalError] = useState("");
  const [actionError, setActionError] = useState("");

  const handleUpdatePayment = async () => {
    setIsLoadingPortal(true);
    setPortalError("");
    try {
      const data = await createPaymentUpdateSession();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Failed to open payment portal:", err);
      setPortalError("Unable to open payment portal. Please try again.");
    } finally {
      setIsLoadingPortal(false);
    }
  };

  const handleStartSubscription = async () => {
    setIsLoadingCheckout(true);
    setActionError("");
    try {
      const data = await createCheckoutSession({ plan: selectedPlan });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Failed to create checkout session:", err);
      setActionError("Unable to start checkout. Please try again.");
    } finally {
      setIsLoadingCheckout(false);
    }
  };

  const status = statusConfig[pharmacy.subscription_status] || {
    label: pharmacy.subscription_status,
    variant: "outline" as const,
    description: "Unknown subscription status",
  };

  const trialDaysRemaining = getDaysRemaining(pharmacy.trial_ends_at);
  const subscriptionDaysRemaining = getDaysRemaining(
    pharmacy.subscription_ends_at
  );

  const showStartSubscription = ["trial", "expired", "pending"].includes(
    pharmacy.subscription_status
  );
  const isMonthly =
    pharmacy.subscription_status === "active" &&
    billing?.plan?.interval === "month";

  // Pricing calculations
  const monthlyPrice = prices?.monthly.amount ?? 0;
  const yearlyTotal = prices?.yearly.amount ?? 0;
  const yearlyMonthly = Math.round(yearlyTotal / 12);
  const annualSavings = monthlyPrice * 12 - yearlyTotal;
  const currency = prices?.monthly.currency ?? "usd";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-500/10">
            <CreditCard className="h-5 w-5 text-violet-600" />
          </div>
          <div>
            <CardTitle>Plan Overview</CardTitle>
            <CardDescription>
              Your current subscription plan and status
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Banner */}
        <div className="flex items-center justify-between p-4 rounded-xl border bg-muted/30">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">Current Plan</p>
              <Badge variant={status.variant}>{status.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {status.description}
            </p>
          </div>
        </div>

        {/* Plan Details */}
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-20 rounded-xl" />
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {billing?.plan && (
              <div className="flex items-start gap-3 p-4 rounded-xl border">
                <CreditCard className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">
                    {billing.plan.name} Plan
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formatAmount(billing.plan.amount, billing.plan.currency)}/
                    {billing.plan.interval}
                  </p>
                </div>
              </div>
            )}

            {pharmacy.subscription_status === "trial" &&
              pharmacy.trial_ends_at && (
                <div className="flex items-start gap-3 p-4 rounded-xl border">
                  <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Trial Ends</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(pharmacy.trial_ends_at)}
                    </p>
                    {trialDaysRemaining !== null && trialDaysRemaining > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {trialDaysRemaining} day
                        {trialDaysRemaining !== 1 ? "s" : ""} remaining
                      </p>
                    )}
                    {trialDaysRemaining !== null && trialDaysRemaining <= 0 && (
                      <Badge variant="destructive" className="text-xs">
                        Trial Ended
                      </Badge>
                    )}
                  </div>
                </div>
              )}

            {pharmacy.subscription_started_at && (
              <div className="flex items-start gap-3 p-4 rounded-xl border">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Subscription Started</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(pharmacy.subscription_started_at)}
                  </p>
                </div>
              </div>
            )}

            {pharmacy.subscription_ends_at && (
              <div className="flex items-start gap-3 p-4 rounded-xl border">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Current Period Ends</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(pharmacy.subscription_ends_at)}
                  </p>
                  {subscriptionDaysRemaining !== null &&
                    subscriptionDaysRemaining > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {subscriptionDaysRemaining} day
                        {subscriptionDaysRemaining !== 1 ? "s" : ""} remaining
                      </p>
                    )}
                </div>
              </div>
            )}

            {billing?.cancelAtPeriodEnd && (
              <div className="flex items-start gap-3 p-4 rounded-xl border border-destructive/50 bg-destructive/5">
                <Clock className="h-5 w-5 text-destructive mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-destructive">
                    Cancellation Scheduled
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Your plan will be canceled at the end of the current billing
                    period.
                  </p>
                </div>
              </div>
            )}

            {/* Payment Method */}
            <div className="flex items-start gap-3 p-4 rounded-xl border">
              <CreditCard className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">Payment Method</p>
                {paymentMethod ? (
                  <p className="text-sm text-muted-foreground">
                    {formatBrand(paymentMethod.brand)} ending in{" "}
                    {paymentMethod.last4} &middot; Expires{" "}
                    {String(paymentMethod.expMonth).padStart(2, "0")}/
                    {paymentMethod.expYear}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No payment method on file
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Start Subscription CTA — for trial, expired, or pending users */}
        {showStartSubscription && prices && (
          <div className="rounded-xl border border-primary/20 bg-primary-soft p-5 space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">
                {pharmacy.subscription_status === "trial"
                  ? "Subscribe now to keep your data when your trial ends"
                  : "Subscribe to activate your account"}
              </p>
              <p className="text-xs text-muted-foreground">
                Choose your billing cycle. You can change it anytime.
              </p>
            </div>

            {/* Plan Toggle */}
            <div className="flex gap-3">
              <button
                onClick={() => setSelectedPlan("monthly")}
                className={`flex-1 rounded-lg border-2 p-3 text-left transition-colors ${
                  selectedPlan === "monthly"
                    ? "border-primary bg-surface"
                    : "border-transparent bg-surface/60 hover:bg-surface"
                }`}
              >
                <p className="text-sm font-semibold text-foreground">Monthly</p>
                <p className="text-lg font-bold text-foreground">
                  {formatAmount(monthlyPrice, currency)}
                  <span className="text-xs font-normal text-muted-foreground">
                    /mo
                  </span>
                </p>
              </button>
              <button
                onClick={() => setSelectedPlan("yearly")}
                className={`flex-1 rounded-lg border-2 p-3 text-left transition-colors relative ${
                  selectedPlan === "yearly"
                    ? "border-primary bg-surface"
                    : "border-transparent bg-surface/60 hover:bg-surface"
                }`}
              >
                {annualSavings > 0 && (
                  <span className="absolute -top-2.5 right-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-secondary text-white">
                    Save {formatAmount(annualSavings, currency)}/yr
                  </span>
                )}
                <p className="text-sm font-semibold text-foreground">Annual</p>
                <p className="text-lg font-bold text-foreground">
                  {formatAmount(yearlyMonthly, currency)}
                  <span className="text-xs font-normal text-muted-foreground">
                    /mo
                  </span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Billed as {formatAmount(yearlyTotal, currency)}/year
                </p>
              </button>
            </div>

            {actionError && (
              <p className="text-sm text-destructive">{actionError}</p>
            )}

            <Button
              onClick={handleStartSubscription}
              disabled={isLoadingCheckout}
              className="w-full"
            >
              {isLoadingCheckout ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redirecting to checkout...
                </>
              ) : (
                <>
                  Start Subscription
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Upgrade to Annual CTA — for active monthly subscribers */}
        {isMonthly && prices && annualSavings > 0 && (
          <div className="rounded-xl border border-secondary/30 bg-secondary-soft p-5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary/10">
                <Sparkles className="h-4 w-4 text-secondary" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">
                  Switch to annual billing and save{" "}
                  {formatAmount(annualSavings, currency)} a year
                </p>
                <p className="text-xs text-muted-foreground">
                  Pay {formatAmount(yearlyMonthly, currency)}/mo instead of{" "}
                  {formatAmount(monthlyPrice, currency)}/mo — that&apos;s like
                  getting 2 months free.
                </p>
              </div>
            </div>

            <Button
              onClick={() => setIsUpgradeDialogOpen(true)}
              variant="outline"
              className="w-full border-secondary/30 text-secondary hover:bg-secondary/5 hover:text-secondary"
            >
              Switch to Annual — Save {formatAmount(annualSavings, currency)}
              /yr
            </Button>

            <UpgradePlanDialog
              open={isUpgradeDialogOpen}
              onOpenChange={setIsUpgradeDialogOpen}
              onSuccess={onSubscriptionChange}
            />
          </div>
        )}

        {/* Update Payment Button */}
        {hasStripeCustomer && (
          <div className="space-y-2">
            {portalError && (
              <p className="text-sm text-destructive">{portalError}</p>
            )}
            <Button
              onClick={handleUpdatePayment}
              disabled={isLoadingPortal}
              variant="outline"
              size="sm"
            >
              {isLoadingPortal ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Opening payment portal...
                </>
              ) : (
                <>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Update Payment Method
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
